/**
 * TemplateGalleryDialog
 *
 * Centered Fluent UI Dialog (80vw x 80vh) displaying the template catalog
 * with a responsive card grid, search, category/difficulty/sort filters.
 * Replaces the former 360px right-side panel.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
  Input,
  Select,
  Dialog,
  DialogSurface,
  DialogBody,
} from '@fluentui/react-components';
import { ArrowLeft24Regular, Dismiss24Regular, Search24Regular } from '@fluentui/react-icons';
import { useTemplates, type UseTemplatesOptions } from '@/hooks/useTemplates';
import { useTemplateApply } from '@/hooks/useTemplateApply';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useNotificationStore } from '@/stores/notificationStore';
import { telemetryService } from '@/services/telemetryService';
import { thinScrollbarStyles } from '@/styles/scrollbar';
import { TemplateCard } from './TemplateCard';
import { TemplatePreview } from './TemplatePreview';
import { ImportRecipeCard } from './ImportRecipeCard';
import { ImportRecipeView } from './ImportRecipeView';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  surface: {
    width: '80vw',
    maxWidth: '80vw',
    height: '80vh',
    maxHeight: '80vh',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  surfacePreview: {
    width: 'auto',
    maxWidth: '640px',
    height: 'fit-content',
    maxHeight: '80vh',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: 0,
    gap: 0,
    rowGap: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    flexShrink: 0,
  },
  headerTitle: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  filterBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  searchInput: {
    flex: '1 1 240px',
  },
  selectWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  selectLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
  },
  select: {
    width: '100%',
  },
  gridContainer: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    ...thinScrollbarStyles,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    gap: tokens.spacingVerticalM,
  },
  emptyIcon: {
    fontSize: '40px',
    color: tokens.colorNeutralForeground4,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    flexShrink: 0,
  },
  footerText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Imported-card preview — shows the freshly imported template as a gallery
  // card so users see exactly how it will render before opening the details.
  cardPreviewBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
  },
  cardPreviewHint: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },
  cardPreviewCard: {
    width: '280px',
  },
});

// =============================================================================
// PROPS
// =============================================================================

export interface TemplateGalleryDialogProps {
  open: boolean;
  onDismiss: () => void;
  /** When set, the gallery opens directly to this template's preview instead of the grid. */
  initialPreviewTemplateId?: string | undefined;
  /**
   * When true, the preview asks for a workflow name and bubbles `(templateId, workflowName)`
   * up via `onCreateFromTemplate` instead of calling the in-canvas `applyTemplate`. Used by the
   * dashboard "Build from templates" entry point where no workflow exists yet.
   */
  requireWorkflowName?: boolean;
  /** Default value for the workflow-name Input (only used when requireWorkflowName is true). */
  defaultWorkflowName?: string;
  /**
   * Existing workflow names in the app, used to block duplicate names in create-new
   * mode — the create call is an upsert, so a duplicate would silently overwrite.
   */
  existingWorkflowNames?: string[] | undefined;
  /** Pending state for the workflow-creation mutation; disables the action button. */
  isCreating?: boolean;
  /**
   * Called when the user submits the preview in create-new mode. The parent is responsible
   * for creating the workflow and navigating to the designer (where the template is auto-applied).
   */
  onCreateFromTemplate?: (templateId: string, workflowName: string) => void;
}

// =============================================================================
// SORT OPTIONS
// =============================================================================

type SortByOption = 'featured' | 'name-asc' | 'name-desc';

const SORT_OPTIONS: { value: SortByOption; label: string }[] = [
  { value: 'featured', label: 'Featured' },
  { value: 'name-asc', label: 'Name (A-Z)' },
  { value: 'name-desc', label: 'Name (Z-A)' },
];

// =============================================================================
// COMPONENT
// =============================================================================

export const TemplateGalleryDialog: React.FC<TemplateGalleryDialogProps> = ({
  open,
  onDismiss,
  initialPreviewTemplateId,
  requireWorkflowName = false,
  defaultWorkflowName = '',
  existingWorkflowNames,
  isCreating = false,
  onCreateFromTemplate,
}) => {
  const styles = useStyles();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState<SortByOption>('featured');
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [showImportView, setShowImportView] = useState(false);
  // Set right after a successful import: shows the imported template as a
  // gallery card first; clicking the card opens the standard detail preview.
  const [importedCardTemplateId, setImportedCardTemplateId] = useState<string | null>(null);
  const { applyTemplate } = useTemplateApply();
  // Import-from-JSON is the testing loop for the Generate Template flow, so
  // both surfaces are gated by the same feature flag.
  const isImportEnabled = useFeatureFlag('generateTemplate');

  // Track gallery open/close
  useEffect(() => {
    if (open) {
      telemetryService.trackEvent('template.gallery_opened', {
        source: initialPreviewTemplateId ? 'direct-preview' : 'browse',
      });
    }
  }, [open, initialPreviewTemplateId]);

  // When gallery opens with a specific template, jump straight to its preview
  useEffect(() => {
    if (open && initialPreviewTemplateId) {
      setPreviewTemplateId(initialPreviewTemplateId);
    }
    if (!open) {
      setPreviewTemplateId(null);
      setShowImportView(false);
      setImportedCardTemplateId(null);
    }
  }, [open, initialPreviewTemplateId]);

  // Debounce search input by 300ms
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((_: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
    setSearchInput(data.value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(data.value);
      if (data.value.length >= 2) {
        telemetryService.trackEvent('template.search', {
          queryLength: String(data.value.length),
        });
      }
    }, 300);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const { templates, categories, totalCount, getTemplate } = useTemplates(
    useMemo(() => {
      const opts: UseTemplatesOptions = { enabled: open, sortBy };
      if (selectedCategory) opts.category = selectedCategory;
      if (debouncedSearch) opts.searchQuery = debouncedSearch;
      return opts;
    }, [open, selectedCategory, debouncedSearch, sortBy])
  );

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedCategory(val === 'all' ? '' : val);
  }, []);

  const handleSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value as SortByOption);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  const handleClearAllFilters = useCallback(() => {
    handleClearSearch();
    setSelectedCategory('');
  }, [handleClearSearch]);

  const handleCardClick = useCallback(
    (templateId: string) => {
      telemetryService.trackEvent('template.selected', { templateId });
      setPreviewTemplateId(templateId);
    },
    []
  );

  const handlePreviewBack = useCallback(() => {
    setPreviewTemplateId(null);
  }, []);

  const handleImportCardClick = useCallback(() => {
    telemetryService.trackEvent('template.import_opened');
    setShowImportView(true);
  }, []);

  const handleImportBack = useCallback(() => {
    setShowImportView(false);
    setImportedCardTemplateId(null);
  }, []);

  // A successful import registers the template in the imported-template store
  // (merged into useTemplates). Show it as a gallery card first — clicking the
  // card opens the standard detail preview, same as any card in the grid.
  const handleImported = useCallback((templateId: string) => {
    setShowImportView(false);
    setImportedCardTemplateId(templateId);
  }, []);

  // Back from the imported-card preview returns to the import view (e.g. to
  // fix the JSON and re-import).
  const handleCardPreviewBack = useCallback(() => {
    setImportedCardTemplateId(null);
    setShowImportView(true);
  }, []);

  const handleApplyTemplate = useCallback(
    (templateId: string, workflowName?: string) => {
      // Create-new mode: defer to parent; it owns the create-workflow mutation
      // and the post-create navigation.
      if (requireWorkflowName) {
        if (workflowName && onCreateFromTemplate) {
          onCreateFromTemplate(templateId, workflowName);
        }
        return;
      }

      const template = getTemplate(templateId);
      if (!template) return;
      const result = applyTemplate(template, { silent: true });
      if (result.success) {
        setPreviewTemplateId(null);
        onDismiss();
      } else if (result.error) {
        useNotificationStore.getState().addNotification({
          type: 'error',
          title: 'Template apply failed',
          message: result.error,
        });
      }
    },
    [getTemplate, applyTemplate, onDismiss, requireWorkflowName, onCreateFromTemplate]
  );

  const footerText = useMemo(() => {
    const showing = templates.length;
    if (showing === totalCount) return `${totalCount} template${totalCount !== 1 ? 's' : ''}`;
    return `Showing ${showing} of ${totalCount} templates`;
  }, [templates.length, totalCount]);

  const handleOpenChange = useCallback(
    (_: unknown, data: { open: boolean }) => {
      if (!data.open) onDismiss();
    },
    [onDismiss]
  );

  const emptyState = (
    <div className={styles.emptyState}>
      <Search24Regular className={styles.emptyIcon} />
      <Text weight="semibold">No templates found</Text>
      <Text size={200}>
        {debouncedSearch || selectedCategory
          ? 'Try adjusting your search or filters'
          : 'No templates available'}
      </Text>
      {(debouncedSearch || selectedCategory) && (
        <Button
          appearance="subtle"
          size="small"
          onClick={handleClearAllFilters}
        >
          Clear all filters
        </Button>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modalType="modal">
      <DialogSurface
        className={mergeClasses(
          styles.surface,
          (!!previewTemplateId || !!importedCardTemplateId) && styles.surfacePreview
        )}
        aria-label="Template gallery"
      >
        <DialogBody className={styles.body}>
          {previewTemplateId ? (
            <TemplatePreview
              templateId={previewTemplateId}
              onBack={handlePreviewBack}
              onApply={handleApplyTemplate}
              onDismiss={onDismiss}
              requireWorkflowName={requireWorkflowName}
              defaultWorkflowName={defaultWorkflowName}
              existingWorkflowNames={existingWorkflowNames}
              isCreating={isCreating}
              {...(requireWorkflowName ? { submitButtonLabel: 'Use template' } : {})}
            />
          ) : importedCardTemplateId ? (
            (() => {
              const importedTemplate = getTemplate(importedCardTemplateId);
              return (
                <>
                  <div className={styles.header}>
                    <Button
                      appearance="subtle"
                      icon={<ArrowLeft24Regular />}
                      onClick={handleCardPreviewBack}
                      aria-label="Back to import"
                    />
                    <Text as="h2" className={styles.headerTitle}>
                      Imported template
                    </Text>
                    <Button
                      appearance="subtle"
                      icon={<Dismiss24Regular />}
                      onClick={onDismiss}
                      aria-label="Close template gallery"
                    />
                  </div>
                  <div className={styles.cardPreviewBody}>
                    {importedTemplate ? (
                      <>
                        <Text className={styles.cardPreviewHint}>
                          This is how your recipe will appear in the gallery. Select the
                          card to see its details and use it.
                        </Text>
                        <div className={styles.cardPreviewCard}>
                          <TemplateCard
                            template={importedTemplate}
                            onClick={handleCardClick}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <Text weight="semibold">Imported template not found</Text>
                        <Button onClick={handleCardPreviewBack}>Back to import</Button>
                      </>
                    )}
                  </div>
                </>
              );
            })()
          ) : showImportView ? (
            <ImportRecipeView
              onBack={handleImportBack}
              onDismiss={onDismiss}
              onImported={handleImported}
            />
          ) : (
            <>
              {/* Header */}
              <div className={styles.header}>
                <Text as="h2" className={styles.headerTitle}>
                  Templates Gallery
                </Text>
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={onDismiss}
                  aria-label="Close template gallery"
                />
              </div>

              {/* Filter bar */}
              <div className={styles.filterBar}>
                <Input
                  className={styles.searchInput}
                  placeholder="Search by name, action, or service..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  contentBefore={<Search24Regular />}
                  contentAfter={
                    searchInput ? (
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Dismiss24Regular />}
                        onClick={handleClearSearch}
                        aria-label="Clear search"
                      />
                    ) : null
                  }
                  aria-label="Search templates"
                />

                <div className={styles.selectWrapper}>
                  <Text className={styles.selectLabel}>Category</Text>
                  <Select
                    className={styles.select}
                    value={selectedCategory || 'all'}
                    onChange={handleCategoryChange}
                    aria-label="Filter by category"
                  >
                    {categories.map((cat) => (
                      <option key={cat.key} value={cat.key}>
                        {cat.label} ({cat.count})
                      </option>
                    ))}
                  </Select>
                </div>

                <div className={styles.selectWrapper}>
                  <Text className={styles.selectLabel}>Sort by</Text>
                  <Select
                    className={styles.select}
                    value={sortBy}
                    onChange={handleSortChange}
                    aria-label="Sort templates"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Body — scrollable card grid. The import card is pinned first
                  and stays visible regardless of search/category filters. */}
              <div className={styles.gridContainer}>
                {templates.length === 0 && !isImportEnabled ? (
                  emptyState
                ) : (
                  <>
                    <div className={styles.grid}>
                      {isImportEnabled && <ImportRecipeCard onClick={handleImportCardClick} />}
                      {templates.map((template) => (
                        <TemplateCard
                          key={template.id}
                          template={template}
                          onClick={handleCardClick}
                        />
                      ))}
                    </div>
                    {templates.length === 0 && emptyState}
                  </>
                )}
              </div>

              {/* Footer */}
              <div className={styles.footer}>
                <Text className={styles.footerText}>{footerText}</Text>
              </div>
            </>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

TemplateGalleryDialog.displayName = 'TemplateGalleryDialog';
